"use client"

import { useEffect, useRef, useState, Suspense } from "react"
import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { WateringForm } from "@/components/tools/watering-form"
import { HeadlineHero, LawnPrefillBanner, ToolDataChartSkeleton, RelatedTools, ResultSuccessCard, ResultEmailCaptureCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { Droplets, CalendarCheck, Timer, Leaf, Repeat, CloudRain, Gauge, BadgeCheck } from "lucide-react"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useDefaultLawn, getCurrentSeason } from "@/hooks/use-default-lawn"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"
import type { WateringScheduleResult } from "@/lib/watering-calculator"

const WateringDataChart = dynamic(
  () => import("@/components/tools/charts/watering-data-chart").then((m) => m.WateringDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={300} /> }
)

function WateringScheduleContent() {
  const searchParams = useSearchParams()
  useFromTool("watering-schedule")
  const { showNewsletterModal, closeNewsletterModal, usageCount, trackUsage } = useNewsletterPrompt()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const [liveResult, setLiveResult] = useState<WateringScheduleResult | null>(null)
  const trackedUsageRef = useRef(false)

  const handleResultChange = (result: WateringScheduleResult | null) => {
    setLiveResult(result)
    if (result && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      // `calculator_used` is fired by the form's own debounced tracker
      // (hooks/useCalculatorAnalytics via components/tools/watering-form.tsx),
      // which carries the richer payload. Firing here too double-counted it.
      trackUsage("watering-schedule")
    }
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Watering Schedule" },
          ]}
        />

        <div className="max-w-5xl mx-auto">
          {/* Hero — matches sister tools */}
          <div className="mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Personalized for your ZIP"
              title="When and how much should you"
              accent="water?"
              subtitle="Free lawn watering schedule calculator by ZIP. Personalized inches-per-week, frequency, zone runtime, and optional catch-can calibration by grass type, climate, and recent rainfall."
            />
          </div>

          {/* Form (live preview baked in — the only results UI on this page) */}
          <div className="mb-8">
            <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
            <WateringForm
              initialValues={{
                zipCode: lawnPrefill.zipCode,
                grassType: lawnPrefill.grassType,
                lawnSize: lawnPrefill.lawnSizeSqft,
                soilType: lawnPrefill.soilType,
                sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
                season: getCurrentSeason(),
              }}
              onResultChange={handleResultChange}
            />
          </div>

          {liveResult && (
            <div className="mt-8 space-y-6">
              <WateringSuccessCard result={liveResult} />
              <ResultEmailCaptureCard
                source="tool_result_watering"
                toolDisplayName="watering schedule"
                subcopy={`Your 12-month ${liveResult.grassType} lawn calendar, starting with this month's tasks. Arrives right away.`}
                grassType={liveResult.grassType}
                zipCode={lawnPrefill.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("watering-schedule", {
                    grassType: liveResult.grassType,
                    zip: lawnPrefill.zipCode,
                    lawnSize: liveResult.lawnSize,
                  }),
                  headline: `Save my watering schedule for ${liveResult.grassType}`,
                  subcopy: "Keep this schedule in a free account and track your lawn over time.",
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_watering"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />
              <WateringGuidanceCard result={liveResult} />
              <WateringDataChart result={liveResult} />
              <ProductGuideCta source="watering_schedule_result" variant="result" />
              {/* Peak-end placement (was buried below RelatedTools). Reframed from
                  a generic "smart watering products" list to an upgrade tied to the
                  schedule they just built — the purchase angle for a low-intent
                  info tool is automation, not necessity. */}
              <ProductRecommendations
                categories={["Watering & Irrigation"]}
                title="Automate the schedule you just built"
                description="Smart timers and sprinklers that run this watering plan for you."
                trackingContext="watering-schedule-result"
                compact
              />
            </div>
          )}

          {/* Pre-submission surface — mirror herbicide-timing so pre-conversion
              visitors see products above RelatedTools, not buried below it. */}
          {!liveResult && (
            <div className="mb-8">
              <ProductRecommendations
                categories={["Watering & Irrigation"]}
                title="Smart Watering Products"
                description="Sprinkler controllers and moisture sensors to dial in your schedule"
                trackingContext="watering-schedule-pre-submission"
                compact
              />
            </div>
          )}

          <RelatedTools
            currentToolId="watering-schedule"
            prefillValues={{
              zip: lawnPrefill.zipCode,
              grassType: liveResult?.grassType ?? lawnPrefill.grassType,
              lawnSize: liveResult?.lawnSize ?? lawnPrefill.lawnSizeSqft,
              soilType: liveResult?.soilType ?? lawnPrefill.soilType,
              sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
              zones: liveResult?.zoneCount,
            }}
          />

        </div>
      </Reveal>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Watering Schedule Generator"
        usageCount={usageCount}
        grassType={liveResult?.grassType}
        zipCode={lawnPrefill.zipCode}
      />
    </div>
  )
}

const WATERING_SEASON_LABELS: Record<WateringScheduleResult["season"], string> = {
  spring: "this spring",
  summer: "this summer",
  fall: "this fall",
  winter: "this winter",
}

const WATERING_SEASON_TITLES: Record<WateringScheduleResult["season"], string> = {
  spring: "Spring watering",
  summer: "Summer watering",
  fall: "Fall watering",
  winter: "Winter watering",
}

/**
 * Explainability companion to the result hero. The watering engine already
 * computes a partial-rainfall signal, cycle-and-soak timing, and season-specific
 * advice for every result (lib/watering-calculator.ts), but the live page only
 * ever showed the number. This surfaces the "this week" adjustment (recent rain
 * that partially — not fully — covers the need), the "how" (split runtime so soil
 * absorbs instead of running off), and the "when" (season-appropriate adjustment)
 * right under the peak figure — turning a bare in/wk value into an actionable
 * plan. The schedule fields are the durable weekly plan; rainfallSubtracted is
 * the engine's documented "we adjusted this week" signal (full skips are handled
 * by the success card itself). The calibration row always renders (every result
 * has an application-rate source); the rain/cycle/season rows are conditional.
 */
function WateringGuidanceCard({ result }: { result: WateringScheduleResult }) {
  const { cycleAndSoak, seasonalAdvice, rainfallSubtracted, skipThisWeek } = result
  const showRain = rainfallSubtracted > 0 && !skipThisWeek
  const showCycleSoak = Boolean(cycleAndSoak?.recommended)
  const showSeasonal = Boolean(seasonalAdvice)

  // Calibration is always relevant: the runtime can be ~2x off between a system
  // estimate and a measured catch-can rate, and the result never said which.
  const measuredRate = result.applicationRateSource !== "system-estimate"
  const calibrationBody = measuredRate
    ? result.applicationRateSource === "catch-can-by-zone"
      ? "Each zone's runtime is dialed in from your catch-can measurements — the most accurate setting."
      : "This runtime is dialed in to your measured catch-can output."
    : `This runtime uses a typical output for your ${result.irrigationSystem.toLowerCase()}. For a precise number, run a 15-minute catch-can test and switch Sprinkler Output to By zone.`

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
      <h3 className="mb-4 text-base md:text-lg font-semibold text-gray-900">
        Dial in your schedule
      </h3>
      <div className="space-y-3">
        {showRain && (
          <div className="flex gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100">
              <CloudRain className="h-5 w-5 text-sky-700" aria-hidden="true" />
            </div>
            <div className="text-sm text-sky-900">
              <p className="font-semibold">About {rainfallSubtracted.toFixed(2)}&Prime; of rain this week</p>
              <p className="mt-0.5">
                Recent rainfall already covers part of your lawn&apos;s need, so you can water a
                little less this week and resume your full schedule once the ground dries out.
              </p>
            </div>
          </div>
        )}
        {showCycleSoak && (
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
              <Repeat className="h-5 w-5 text-amber-700" aria-hidden="true" />
            </div>
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Cycle &amp; soak to stop runoff</p>
              <p className="mt-0.5">
                Split your longest zone into {cycleAndSoak.cycles} cycles of about{" "}
                {cycleAndSoak.runMinutesPerCycle} min, pausing {cycleAndSoak.soakMinutes} min
                between starts so the soil can absorb the water instead of letting it run off.
              </p>
            </div>
          </div>
        )}
        {showSeasonal && (
          <div className="flex gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-100">
              <Leaf className="h-5 w-5 text-green-700" aria-hidden="true" />
            </div>
            <div className="text-sm text-green-900">
              <p className="font-semibold">{WATERING_SEASON_TITLES[result.season]}</p>
              <p className="mt-0.5">{seasonalAdvice}</p>
            </div>
          </div>
        )}
        <div className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100">
            {measuredRate ? (
              <BadgeCheck className="h-5 w-5 text-slate-600" aria-hidden="true" />
            ) : (
              <Gauge className="h-5 w-5 text-slate-600" aria-hidden="true" />
            )}
          </div>
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-slate-900">
              {measuredRate ? "Calibrated to your catch-can test" : "Based on a system estimate"}
            </p>
            <p className="mt-0.5">{calibrationBody}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Bounded peak-moment frame for the watering result. Hero = inches per week
 * (the single most useful number). Interpretation names grass, size, and
 * season. Skip-this-week rainfall cases reframe to an intentional hold.
 * Next-action: mowing calculator (mow height and watering depth go together).
 */
export function formatWateringRecheckDate(referenceDate: Date) {
  const date = new Date(referenceDate)
  date.setDate(date.getDate() + 7)
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  })
}

function WateringSuccessCard({ result }: { result: WateringScheduleResult }) {
  const [recheckDate, setRecheckDate] = useState<string | null>(null)
  useEffect(() => setRecheckDate(formatWateringRecheckDate(new Date())), [])
  const seasonLabel = WATERING_SEASON_LABELS[result.season]
  const inPerWk = result.soilAdjustedWater.toFixed(1)
  const galPerWk = Math.round(result.weeklyGallons).toLocaleString()
  const sizeLabel = result.lawnSize.toLocaleString()

  const mowingHref = buildRelatedToolUrl("mowing-calculator", "watering-schedule", {
    grassType: result.grassType,
    season: result.season,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "watering-schedule",
      targetTool: "mowing-calculator",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Mowing height and watering depth go hand in hand",
      prefillFields: ["grass", "season"].join(","),
    })
  }

  const share = {
    // grass + size round-trip (WateringForm reads ?grass/?size from the URL),
    // but soilType is dropped: nothing reads ?soil back into the form, so it
    // would silently reconstruct the wrong soil.
    url: buildToolUrl("watering-schedule", {
      grassType: result.grassType,
      lawnSize: result.lawnSize,
    }),
    title: "Watering Schedule",
    text: `Watering schedule for my ${result.grassType} lawn`,
  }

  if (result.skipThisWeek && result.rainfallSubtracted <= 0) {
    return (
      <ResultSuccessCard
        eyebrow="Lawn is dormant"
        heroValue="0"
        heroUnit="gal"
        heroLabel="needed this week"
        stats={[
          { icon: Leaf, label: "Season", value: seasonLabel },
          { icon: CalendarCheck, label: "Water again", value: "Next growing season" },
        ]}
        interpretation={
          <>Your {result.grassType} lawn is dormant {seasonLabel}, so irrigation is not recommended until the next growing season.</>
        }
        cta={{
          href: mowingHref,
          label: "Check your mowing height",
          onClick: handleCtaClick,
        }}
        share={share}
      />
    )
  }

  if (result.skipThisWeek) {
    const recheckLabel = recheckDate ?? "Next week"
    return (
      <ResultSuccessCard
        eyebrow="Rain covered your lawn"
        heroValue="0"
        heroUnit="gal"
        heroLabel="needed this week"
        stats={[
          { icon: CloudRain, label: "Recent rain", value: `${result.rainfallSubtracted.toFixed(1)} in` },
          { icon: Droplets, label: "Dry-week baseline", value: `${galPerWk} gal` },
          { icon: CalendarCheck, label: "Check again", value: recheckLabel },
        ]}
        interpretation={
          <>Recent rainfall already covered your {sizeLabel} sq ft {result.grassType} lawn. Recheck the soil {recheckDate ? `on ${recheckDate}` : "next week"}. If it has dried out, resume the usual {inPerWk} in/wk ({galPerWk} gal) dry-week schedule.</>
        }
        cta={{
          href: mowingHref,
          label: "Check your mowing height",
          onClick: handleCtaClick,
        }}
        share={share}
      />
    )
  }

  return (
    <ResultSuccessCard
      heroValue={inPerWk}
      heroUnit="in/wk"
      heroLabel="of water needed"
      reframe={{
        ariaLabel: "Show water need in inches or gallons per week",
        defaultKey: "inches",
        analyticsEvent: "watering_view_toggled",
        analyticsProps: { grass_type: result.grassType, season: result.season },
        options: [
          { key: "inches", label: "Inches" },
          { key: "gallons", label: "Gallons" },
        ],
        values: {
          inches: { value: inPerWk, unit: "in/wk", label: "of water needed" },
          gallons: { value: galPerWk, unit: "gal/wk", label: "across your whole lawn" },
        },
      }}
      stats={[
        { icon: Droplets, label: "Per week", value: `${galPerWk} gal` },
        { icon: CalendarCheck, label: "Days / week", value: `${result.sessionsPerWeek}x` },
        { icon: Timer, label: "Per session", value: `${result.sessionDuration} min` },
      ]}
      interpretation={
        <>
          Your {sizeLabel} sq ft {result.grassType} lawn needs {inPerWk} in/wk {seasonLabel} across {result.sessionsPerWeek} {result.sessionsPerWeek === 1 ? "session" : "sessions"} ({result.sessionDuration} min each).
        </>
      }
      cta={{
        href: mowingHref,
        label: "Check your mowing height",
        onClick: handleCtaClick,
      }}
      share={share}
    />
  )
}

export function WateringSchedulePageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="watering-schedule" />}>
      <WateringScheduleContent />
    </Suspense>
  )
}
